import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import {
  ENTITY_TYPES,
  useCreateEntity,
  type EntityDto,
  type EntitySuggestionDto,
  type EntityTypeSlug,
} from '../../api/queries/entities';

interface Props {
  open: boolean;
  onClose: () => void;
  bookId?: string;
  onCreated?: (entity: EntityDto) => void;
}

export function EntityCreateDialog({
  open,
  onClose,
  bookId,
  onCreated,
}: Props) {
  const create = useCreateEntity();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entityType, setEntityType] = useState<EntityTypeSlug>('character');
  const [suggestions, setSuggestions] = useState<EntitySuggestionDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setEntityType('character');
    setSuggestions([]);
    setError(null);
  };

  const handleSubmit = async (skipDuplicateCheck = false) => {
    if (!name.trim()) return;
    setError(null);
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        entityType,
        description: description.trim() || undefined,
        projectId: bookId ?? null,
        libraryScope: 'private',
        skipDuplicateCheck,
      });
      if (result.kind === 'duplicates') {
        setSuggestions(result.suggestions);
        return;
      }
      onCreated?.(result.entity);
      reset();
      onClose();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>New entity</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <FilterableSelect
            options={ENTITY_TYPES.map((t) => ({
              value: t.value,
              label: t.label,
            }))}
            value={entityType}
            onChange={(newValue) => setEntityType(newValue as EntityTypeSlug)}
            label="Type"
            allowEmpty={false}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
          />

          {suggestions.length > 0 && (
            <>
              <Alert severity="warning">
                We found similar entities in your library. Use one of these or
                create anyway.
              </Alert>
              <List dense>
                {suggestions.map((s) => (
                  <ListItemButton key={s.id} disabled>
                    <ListItemText
                      primary={
                        <Typography variant="body2">
                          {s.name} ({s.entityType}) —{' '}
                          {Math.round(s.score * 100)}% match
                        </Typography>
                      }
                      secondary={s.description ?? ''}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {suggestions.length > 0 && (
          <Button onClick={() => handleSubmit(true)}>Create anyway</Button>
        )}
        <Button
          variant="contained"
          onClick={() => handleSubmit(false)}
          disabled={!name.trim() || create.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
